import React from 'react';
import { Page, UseCase } from '../types';

interface UseCaseCardProps { 
    title: string; 
    description: string; 
    imageUrl: string; 
    onClick: () => void;
}

const UseCaseCard: React.FC<UseCaseCardProps> = ({ title, description, imageUrl, onClick }) => (
  <button onClick={onClick} className="text-left w-full glass-effect rounded-xl overflow-hidden group transform transition-all duration-300 hover:-translate-y-2 hover:shadow-xl border border-slate-200 dark:border-zinc-800 hover:border-[#05F564]/50">
    <div className="overflow-hidden">
        <img src={imageUrl} alt={title} className="w-full h-48 object-cover group-hover:scale-105 transition-transform duration-500" />
    </div>
    <div className="p-6">
      <h3 className="text-xl font-bold text-slate-900 dark:text-white">{title}</h3>
      <p className="mt-2 text-slate-600 dark:text-zinc-300">{description}</p>
    </div>
  </button>
);

interface UseCasesPageProps {
    navigateToUseCase: (useCase: UseCase) => void;
}

const UseCasesPage: React.FC<UseCasesPageProps> = ({ navigateToUseCase }) => {
  const useCases = [
    { id: UseCase.ECommerce, title: "E-commerce", description: "Provide 24/7 product support, track orders, handle returns, and recover abandoned carts by sending timely reminders and offers.", imageUrl: "https://images.unsplash.com/photo-1556742502-ec7c0e9f34b1?q=80&w=600&h=400&fit=crop&crop=entropy" },
    { id: UseCase.RealEstate, title: "Real Estate", description: "Instantly answer property inquiries, schedule viewings, qualify potential buyers, and send new listing alerts to interested clients.", imageUrl: "https://images.unsplash.com/photo-1580587771525-78b9dba3b914?q=80&w=600&h=400&fit=crop&crop=entropy" },
    { id: UseCase.Healthcare, title: "Healthcare", description: "Automate appointment scheduling and reminders, answer common patient questions, and provide pre-visit instructions securely.", imageUrl: "https://images.unsplash.com/photo-1576091160550-2173dba999ef?q=80&w=600&h=400&fit=crop&crop=entropy" },
    { id: UseCase.Hospitality, title: "Hospitality", description: "Manage bookings, answer questions about amenities, offer room service, and send personalized local recommendations to guests.", imageUrl: "https://images.unsplash.com/photo-1566073771259-6a8506099945?q=80&w=600&h=400&fit=crop&crop=entropy" },
    { id: UseCase.Education, title: "Education", description: "Assist with student admissions, provide course information, answer campus-related FAQs, and send important announcements to students.", imageUrl: "https://images.unsplash.com/photo-1541339907198-e08756dedf3f?q=80&w=600&h=400&fit=crop&crop=entropy" },
    { id: UseCase.FinancialServices, title: "Financial Services", description: "Offer basic account support, explain product features, capture leads for loans or insurance, and schedule consultations with advisors.", imageUrl: "https://images.unsplash.com/photo-1553729459-efe14ef6055d?q=80&w=600&h=400&fit=crop&crop=entropy" },
  ];

  return (
    <div className="py-20 animate-fadeIn">
      <div className="container mx-auto px-4">
        <div className="text-center mb-16">
          <h1 className="text-4xl md:text-5xl font-extrabold text-slate-900 dark:text-white">Powerful Solutions for Every Industry</h1>
          <p className="mt-4 max-w-2xl mx-auto text-lg text-slate-600 dark:text-zinc-300">
            Discover how Wampia's AI agents can be adapted to meet the unique challenges of your business.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {useCases.map(uc => (
            <UseCaseCard
              key={uc.id}
              title={uc.title}
              description={uc.description}
              imageUrl={uc.imageUrl}
              onClick={() => navigateToUseCase(uc.id)}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export default UseCasesPage;